import React, { useState, useRef, useEffect, useContext } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled, { ThemeContext } from "styled-components"
import { space } from "styled-system"

import Caret from "../icons/Caret"
import Box from "../atoms/Box"
import { Text } from "../atoms/Typography"
import { AlwaysOpenMegaMenu, PopperMegaMenu } from "./MegaMenuWrappers"
import { MegaMenuContent } from "./MegaMenuContent"

const MegaContainer = styled(Box)`
  position: relative;
  display: inline-block;
  ${space}
`

const TriggerButton = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
`

const NavMegaMenu = ({
  name,
  linkTitle,
  columnsOnGrid = "three",
  dropdownStyle = "full-width",
  showDescription = false,
  showIcon = false,
  iconSize = 32,
  columnSpacing = 24,
  megaMenuColumns = [],
  dropDownOpenOn = "hover",
  card = {},
  iconLocation = "left",
  forceOpen = false,
  ...rest
}) => {
  linkTitle = linkTitle?.length > 0 ? linkTitle : "Mega Menu"
  const theme = useContext(ThemeContext)
  const [open, setOpen] = useState(false)
  const [isCaretHovered, setIsCaretHovered] = useState(false)
  const anchorRef = useRef(null)

  // Track previous open state
  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus()
    }
    prevOpen.current = open
  }, [open])

  // Toggle logic
  const handleToggle = () => {
    if (!forceOpen && dropDownOpenOn === "click") {
      setOpen(prev => !prev)
    }
  }
  const handleMouseEnter = () => {
    if (!forceOpen && dropDownOpenOn === "hover") {
      setOpen(true)
    }
    setIsCaretHovered(true)
  }
  const handleMouseLeave = () => {
    if (!forceOpen && dropDownOpenOn === "hover") {
      setOpen(false)
    }
    setIsCaretHovered(false)
  }
  const handleClose = evt => {
    if (!forceOpen && anchorRef.current?.contains(evt.target)) {
      return
    }
    if (!forceOpen) {
      setOpen(false)
    }
  }
  const handleListKeyDown = event => {
    if (!forceOpen && event.key === "Tab") {
      event.preventDefault()
      setOpen(false)
    }
  }

  // Update caret color based on hover vs open
  const variant = "navLink"
  const navLinkTypography = theme?.typography?.[variant]
  const defaultCaretColor = navLinkTypography?.color || theme.colors.text
  const caretHoverColor =
    navLinkTypography?.["& :hover"]?.color || defaultCaretColor
  const caretActiveColor =
    navLinkTypography?.["& :active"]?.color || defaultCaretColor

  let currentCaretColor = defaultCaretColor
  if (open && isCaretHovered) currentCaretColor = caretHoverColor
  else if (open) currentCaretColor = caretActiveColor
  else if (isCaretHovered) currentCaretColor = caretHoverColor

  const menuTopOffset = 64
  const popperStyle =
    dropdownStyle === "full-width"
      ? {
          position: "fixed",
          top: menuTopOffset,
          left: 0,
          right: 0,
          width: "100%",
          maxWidth: "100vw",
          overflowX: "visible",
          boxSizing: "border-box",
          zIndex: 200,
        }
      : {
          position: "fixed",
          top: menuTopOffset,
          left: "6rem",
          right: "6rem",
          width: "calc(100vw - 12rem)",
          maxWidth: "100vw",
          overflowX: "visible",
          boxSizing: "border-box",
          zIndex: 200,
        }

  // For live editing preview, immediately show mega menu content.
  if (forceOpen) {
    return (
      <AlwaysOpenMegaMenu card={card}>
        <MegaMenuContent
          columnsOnGrid={columnsOnGrid}
          megaMenuColumns={megaMenuColumns}
          showDescription={showDescription}
          showIcon={showIcon}
          iconSize={iconSize}
          iconLocation={iconLocation}
          columnSpacing={columnSpacing}
          forceOpen={forceOpen}
        />
      </AlwaysOpenMegaMenu>
    )
  }

  // Otherwise, render navlink button attached to popper used for Gatsby Site
  return (
    <MegaContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <TriggerButton
        ref={anchorRef}
        onClick={handleToggle}
        aria-haspopup="true"
      >
        <Text variant="navLink">
          {linkTitle}
          <Caret
            color={currentCaretColor}
            direction={open ? "up" : "down"}
            ml={1}
            width="16px"
            height="16px"
          />
        </Text>
      </TriggerButton>

      <PopperMegaMenu
        open={open}
        card={card}
        anchorEl={null}
        handleClose={handleClose}
        handleListKeyDown={handleListKeyDown}
        popperStyle={popperStyle}
        disablePortal={false}
      >
        <MegaMenuContent
          columnsOnGrid={columnsOnGrid}
          megaMenuColumns={megaMenuColumns}
          showDescription={showDescription}
          showIcon={showIcon}
          iconSize={iconSize}
          iconLocation={iconLocation}
          columnSpacing={columnSpacing}
        />
      </PopperMegaMenu>
    </MegaContainer>
  )
}

NavMegaMenu.propTypes = {
  name: PropTypes.string,
  linkTitle: PropTypes.string,
  columnsOnGrid: PropTypes.oneOf(["two", "three", "four", "five"]),
  dropdownStyle: PropTypes.oneOf(["full-width", "floating"]),
  showDescription: PropTypes.bool,
  showIcon: PropTypes.bool,
  iconSize: PropTypes.number,
  columnSpacing: PropTypes.number,
  megaMenuColumns: PropTypes.array,
  dropDownOpenOn: PropTypes.oneOf(["click", "hover"]),
  card: PropTypes.object,
  iconLocation: PropTypes.oneOf(["top", "bottom", "left"]),
  forceOpen: PropTypes.bool,
}

export default NavMegaMenu

export const query = graphql`
  fragment NavMegaMenu on STRAPI__COMPONENT_UTILS_MEGA_MENU {
    strapi_id
    name
    linkTitle
    columnsOnGrid
    dropdownStyle
    showDescription
    showIcon
    iconSize
    columnSpacing
    megaMenuColumns {
      strapi_id
      linkTitle
      megaMenuLinks {
        strapi_id
        name
        descriptionRichText {
          ...RichText
        }
        iconAttribute {
          image {
            ...Icon
          }
        }
        link {
          ...Link
          linkText
        }
        button {
          ...Button
        }
      }
    }
    card {
      ...Card
    }
    iconLocation
  }
`
